// ════════════════════════════════════════════════════════════════════════════
// LpaButton — botão primitivo com variants, sizes, loading state, a11y
// Pack DS · Primitivos Lpa*
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export type LpaButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type LpaButtonSize = "sm" | "md" | "lg";

export interface LpaButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "type"
> {
  variant?: LpaButtonVariant;
  size?: LpaButtonSize;
  /** Texto/conteúdo do botão */
  children: React.ReactNode;
  /** Ícone à esquerda do texto */
  icon?: React.ReactNode;
  /** Loading state — desabilita + mostra "…" */
  loading?: boolean;
  /** Type HTML (default: "button" para evitar submit acidental) */
  type?: "button" | "submit" | "reset";
  /** Width 100% */
  fullWidth?: boolean;
  testId?: string;
}

const SIZE_PADDING: Record<LpaButtonSize, string> = {
  sm: "5px 10px",
  md: "8px 14px",
  lg: "10px 20px",
};

const SIZE_FONT: Record<LpaButtonSize, string> = {
  sm: "var(--lpa-text-sm)",
  md: "var(--lpa-text-lg)",
  lg: "var(--lpa-text-xl)",
};

function variantStyle(variant: LpaButtonVariant): React.CSSProperties {
  switch (variant) {
    case "primary":
      return {
        background: "var(--lpa-accent)",
        color: "var(--lpa-accent-text)",
        border: "1px solid var(--lpa-accent)",
      };
    case "secondary":
      return {
        background: "transparent",
        color: "var(--lpa-text)",
        border: "1px solid var(--lpa-border)",
      };
    case "ghost":
      return {
        background: "transparent",
        color: "var(--lpa-accent)",
        border: "1px solid transparent",
      };
    case "danger":
      return {
        background: "var(--lpa-danger)",
        color: "#fff",
        border: "1px solid var(--lpa-danger)",
      };
  }
}

const LpaButton = React.forwardRef<HTMLButtonElement, LpaButtonProps>(function LpaButton(
  {
    variant = "primary",
    size = "md",
    children,
    icon,
    loading = false,
    disabled = false,
    type = "button",
    fullWidth = false,
    testId,
    style,
    className,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      data-testid={testId}
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: SIZE_PADDING[size],
        fontSize: SIZE_FONT[size],
        fontWeight: 600,
        fontFamily: "var(--lpa-font-sans)",
        borderRadius: "var(--lpa-radius-md)",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.55 : 1,
        transition: "all var(--lpa-tr-fast)",
        whiteSpace: "nowrap",
        width: fullWidth ? "100%" : undefined,
        outline: "none",
        ...variantStyle(variant),
        ...style,
      }}
      onMouseEnter={(e) => {
        if (isDisabled) return;
        if (variant === "primary") {
          e.currentTarget.style.background = "var(--lpa-accent-hover)";
        } else if (variant === "ghost") {
          e.currentTarget.style.background = "var(--lpa-accent-soft)";
        } else if (variant === "secondary") {
          e.currentTarget.style.borderColor = "var(--lpa-border-strong)";
          e.currentTarget.style.background = "var(--lpa-surface)";
        }
        rest.onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        if (isDisabled) return;
        Object.assign(e.currentTarget.style, variantStyle(variant));
        e.currentTarget.style.background = variantStyle(variant).background as string;
        rest.onMouseLeave?.(e);
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = "0 0 0 3px var(--lpa-focus-ring)";
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
        rest.onBlur?.(e);
      }}
      {...rest}
    >
      {loading ? (
        <span aria-hidden="true">…</span>
      ) : (
        <>
          {icon && (
            <span aria-hidden="true" style={{ display: "inline-flex" }}>
              {icon}
            </span>
          )}
          <span>{children}</span>
        </>
      )}
    </button>
  );
});

export default LpaButton;
